import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, X, AlertCircle, Search } from 'lucide-react';
import { getRoles, createRole, updateRole, deleteRole } from '../api';
import { useAuth } from '../AuthContext';

const emptyForm = {
  name: '',
  display_name: '',
  description: '',
  color: '#6b7280',
};

export default function Role() {
  const { hasMenuAction } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadRoles = () => {
    setLoading(true);
    getRoles()
      .then(res => setRoles(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadRoles(); }, []);

  const filteredRoles = roles.filter(r =>
    !search ||
    r.display_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.name?.toLowerCase().includes(search.toLowerCase()) ||
    r.description?.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setEditingRole(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = (role) => {
    setEditingRole(role);
    setForm({
      name: role.name,
      display_name: role.display_name || '',
      description: role.description || '',
      color: role.color || '#6b7280',
    });
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    setError('');
    if (!form.display_name) { setError('Nama tampilan role harus diisi'); return; }
    if (!editingRole && !form.name) { setError('Nama role harus diisi'); return; }

    setSaving(true);
    try {
      if (editingRole) {
        await updateRole(editingRole.id, {
          display_name: form.display_name,
          description: form.description,
          color: form.color,
        });
      } else {
        await createRole(form);
      }
      setShowModal(false);
      loadRoles();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan role');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Yakin ingin menghapus role "${role.display_name}"?`)) return;
    try {
      await deleteRole(role.id);
      loadRoles();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal menghapus role');
    }
  };

  const presetColors = [
    '#11cdef', '#f59e0b', '#2dce89', '#fb6340', '#8965e0', '#06b6d4',
    '#f5365c', '#5e72e4', '#172b4d', '#6366f1', '#84cc16', '#ec4899',
  ];

  return (
    <div className="page role-page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Master Role</h2>
            <p className="page-desc">{roles.length} role terdaftar dalam sistem</p>
          </div>
          {hasMenuAction('master_role', 'create') && (
            <button className="btn btn-primary" onClick={openCreate}>
              <Plus size={16} />
              <span>Tambah Role</span>
            </button>
          )}
        </div>
      </div>

      <div className="filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Cari role..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="page-loading">Memuat data role...</div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Role</th>
                <th>Nama Tampilan</th>
                <th>Deskripsi</th>
                <th>Warna</th>
                <th style={{ width: 120 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filteredRoles.map(r => (
                <tr key={r.id}>
                  <td>
                    <span className="role-badge" style={{
                      background: (r.color || '#6b7280') + '18',
                      color: r.color || '#6b7280',
                    }}>
                      {r.name}
                    </span>
                  </td>
                  <td><strong>{r.display_name}</strong></td>
                  <td className="text-muted">{r.description || '-'}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        width: 24, height: 24, borderRadius: 6,
                        background: r.color || '#6b7280',
                        display: 'inline-block',
                      }} />
                      <code style={{ fontSize: '0.75rem' }}>{r.color || '#6b7280'}</code>
                    </div>
                  </td>
                  <td>
                    <div className="action-btns">
                      {hasMenuAction('master_role', 'edit') && (
                        <button className="btn-icon" title="Edit" onClick={() => openEdit(r)}>
                          <Edit2 size={14} />
                        </button>
                      )}
                      {hasMenuAction('master_role', 'delete') && r.name !== 'admin' && r.name !== 'staff' && (
                        <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(r)}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredRoles.length === 0 && (
                <tr><td colSpan={5} className="text-center text-muted">Tidak ada data role</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingRole ? 'Edit Role' : 'Tambah Role Baru'}</h3>
              <button className="btn-icon" onClick={() => setShowModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="form-error">
                  <AlertCircle size={14} />
                  <span>{error}</span>
                </div>
              )}

              <div className="form-grid">
                {!editingRole && (
                  <div className="form-group">
                    <label>Nama Role</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                      placeholder="contoh: supervisor"
                    />
                    <small className="form-hint">Digunakan sebagai identifier. Huruf kecil, tanpa spasi.</small>
                  </div>
                )}
                <div className="form-group">
                  <label>Nama Tampilan</label>
                  <input
                    type="text"
                    value={form.display_name}
                    onChange={e => setForm({ ...form, display_name: e.target.value })}
                    placeholder="contoh: Supervisor"
                  />
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Deskripsi</label>
                  <textarea
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                    placeholder="Deskripsi role..."
                    rows={2}
                  />
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Warna</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {presetColors.map(c => (
                      <span
                        key={c}
                        onClick={() => setForm({ ...form, color: c })}
                        style={{
                          width: 32, height: 32, borderRadius: 8, cursor: 'pointer',
                          background: c,
                          border: form.color === c ? '3px solid var(--primary)' : '3px solid transparent',
                          transition: 'border 0.15s',
                        }}
                      />
                    ))}
                    <input
                      type="color"
                      value={form.color}
                      onChange={e => setForm({ ...form, color: e.target.value })}
                      style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', padding: 0 }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
